import produce, { Draft } from 'immer';
import { EMPTY_IMAGE, EMPTY_IMAGE_ENTRY } from '../../constants/image';
import { ImageAction, ImageActionType } from '../../actions';
import { ImageEntry, ImageStatus, ImagesState } from '../../state';
import { cloneDeep } from 'lodash';
import { serializeErrors } from '../serialize-errors';

const initialState = Object.freeze({});

const getImageEntry = <A extends { imageKey: string }>(
  state: Draft<ImagesState>,
  action: A,
): Draft<ImageEntry> => {
  const draft = state[action.imageKey];
  if (!draft) {
    throw new Error('Image not found.');
  }
  return draft;
};

const setImageStatus = <A extends { errors?: Error[]; imageKey: string }>(
  state: Draft<ImagesState>,
  action: A,
  status: ImageStatus,
): void => {
  const entry = getImageEntry(state, action);
  entry.status = status;
  if (action.errors) {
    entry.errors = serializeErrors(action.errors);
  } else {
    entry.errors = null;
  }
};

export const imagesReducer = (
  state: ImagesState = initialState,
  action: ImageAction,
) =>
  produce(state, draft => {
    switch (action.type) {
      case ImageActionType.Load: {
        draft[action.imageKey] = {
          ...EMPTY_IMAGE_ENTRY,
          image: {
            ...EMPTY_IMAGE,
            ...action.image,
          },
        };

        return;
      }

      case ImageActionType.UploadRequest: {
        draft[action.imageKey] =
          draft[action.imageKey] || cloneDeep(EMPTY_IMAGE_ENTRY);
        draft[action.imageKey].uploadProgress = 0;
        return;
      }

      case ImageActionType.UploadError:
        return setImageStatus(draft, action, ImageStatus.UploadError);

      case ImageActionType.UploadSuccess: {
        const entry = getImageEntry(draft, action);
        entry.uploadProgress = 1;
        entry.image = {
          ...entry.image,
          ...action.image,
        };
        return setImageStatus(draft, action, ImageStatus.UploadSuccess);
      }

      case ImageActionType.SetUploadProgress: {
        getImageEntry(draft, action).uploadProgress = action.progress;
      }
    }
  });
